import React, { memo, useCallback, useMemo } from 'react';
import { TableProps, TableColumn, RowKeyType } from '@/types/table';

const cn = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

/** Внутренние пропсы (с некоторыми опциями по-умолчанию) */
interface TableInnerProps<T> extends TableProps<T> {
  rowKey?: RowKeyType<T>;
  skeletonRows?: number;
}

/** Маленькая утиль для безопасного чтения значения по ключу */
const safeGet = (row: unknown, key: string): unknown => {
  if (row && typeof row === 'object') {
    try {
      return (row as Record<string, unknown>)[key];
    } catch {
      return undefined;
    }
  }
  return undefined;
};

/** Skeleton для таблицы */
function TableSkeletonInner<T>({
  columns,
  rowCount = 5,
  compact = false,
}: Readonly<{
  columns: TableColumn<T>[];
  rowCount?: number;
  compact?: boolean;
}>): JSX.Element {
  const colStyles = useMemo(
    () =>
      columns.map((col) => ({
        key: String(col.key),
        style: {
          width: col.width,
          minWidth: col.minWidth,
          maxWidth: col.maxWidth,
        } as React.CSSProperties,
      })),
    [columns],
  );

  const rowIds = useMemo(() => {
    return Array.from({ length: rowCount }, () => crypto.randomUUID());
  }, [rowCount]);

  return (
    <div className="overflow-x-auto bg-white">
      <table className="min-w-full divide-y divide-gray-200" role="table" aria-hidden>
        <thead className="bg-gray-50">
          <tr>
            {colStyles.map((c) => (
              <th
                key={`shead-${c.key}`}
                scope="col"
                style={c.style}
                className={cn(
                  'text-left text-xs font-medium text-gray-500 uppercase tracking-wider select-none',
                  compact ? 'px-4 py-2' : 'px-6 py-3',
                )}
              >
                <div className="h-3 rounded bg-gray-200 w-3/4" />
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="bg-white divide-y divide-gray-200">
          {rowIds.map((rid) => (
            <tr key={`srow-${rid}`} className="animate-pulse">
              {colStyles.map((c) => (
                <td
                  key={`srow-${rid}-cell-${c.key}`}
                  style={c.style}
                  className={cn('align-middle', compact ? 'px-4 py-2' : 'px-6 py-4')}
                >
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
const TableSkeleton = memo(TableSkeletonInner) as typeof TableSkeletonInner;

/** Основной компонент таблицы (generic) */
function TableInner<T>({
  data,
  columns,
  loading = false,
  emptyMessage,
  className = '',
  tableClassName = '',
  headerClassName = '',
  rowClassName,
  onRowClick,
  rowKey,
  skeletonRows = 5,
  striped = false,
  hoverable = true,
  compact = false,
  ariaLabel,
  selectedRowKey,
}: TableInnerProps<T> & {
  ariaLabel?: string;
  selectedRowKey?: string | number;
}): JSX.Element {
  // разрешаем пользователю передать строку ключа без строгой проверки keyof T —
  // внутри мы всё равно делаем fallback на индекс
  const effectiveRowKey = useMemo(() => rowKey ?? ('id' as RowKeyType<T>), [rowKey]);

  const colMeta = useMemo(
    () =>
      columns.map((col) => ({
        col,
        key: String(col.key),
        style: {
          width: col.width,
          minWidth: col.minWidth,
          maxWidth: col.maxWidth,
        } as React.CSSProperties,
        headerClassName: col.headerClassName,
        cellClassName: col.cellClassName,
        align: col.align ?? 'left',
      })),
    [columns],
  );

  const getRowKey = useCallback(
    (row: T, index: number): string | number => {
      if (typeof effectiveRowKey === 'function') {
        try {
          const v = effectiveRowKey(row, index);
          return v ?? index;
        } catch {
          return index;
        }
      }

      if (
        typeof row === 'object' &&
        row !== null &&
        (typeof effectiveRowKey === 'string' || typeof effectiveRowKey === 'number')
      ) {
        const keyName = String(effectiveRowKey);
        const val = safeGet(row, keyName);
        if (val !== undefined && val !== null) return String(val);
      }

      return index;
    },
    [effectiveRowKey],
  );

  const getRowClassNames = useCallback(
    (row: T, index: number): string => {
      const rowKeyValue = getRowKey(row, index);
      const isSelected =
        selectedRowKey !== undefined && String(rowKeyValue) === String(selectedRowKey);

      const base = cn(
        'transition-colors',
        striped && index % 2 === 1 ? 'bg-gray-50' : 'bg-white',
        hoverable && onRowClick ? 'hover:bg-gray-100 cursor-pointer' : '',
        isSelected ? 'bg-blue-50 ring-1 ring-blue-200' : '',
      );

      const additional =
        typeof rowClassName === 'function' ? rowClassName(row, index) : (rowClassName ?? '');

      return cn(base, additional);
    },
    [striped, hoverable, onRowClick, compact, rowClassName, getRowKey, selectedRowKey],
  );

  const isPlainObject = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null;

  const tryCallToString = (v: unknown): string | null => {
    if (!isPlainObject(v)) return null;
    const toStr = v['toString'];
    if (typeof toStr !== 'function') return null;
    try {
      const s = (toStr as (...args: unknown[]) => unknown).call(v);
      if (s && s !== '[object Object]') return String(s);
      return null;
    } catch {
      return null;
    }
  };

  const objectToString = (v: unknown): string => {
    const s = tryCallToString(v);
    if (s) return s;
    try {
      return JSON.stringify(v);
    } catch {
      return '-';
    }
  };

  const formatValue = useCallback((val: unknown): React.ReactNode => {
    if (React.isValidElement(val)) return val;
    if (val instanceof Date) return Number.isNaN(val.getTime()) ? '-' : val.toLocaleString();

    if (isPlainObject(val)) return objectToString(val);

    return String(val);
  }, []);

  const renderCell = useCallback(
    (column: TableColumn<T>, row: T, rowIndex: number): React.ReactNode => {
      const val = safeGet(row, column.key);

      if (typeof column.render === 'function') {
        // предоставляет value, row, index — чтобы пользователь мог переопределить поведение
        return column.render(val, row, rowIndex);
      }

      if (val === null || val === undefined) return '-';

      return formatValue(val);
    },
    [formatValue],
  );

  const handleRowClick = useCallback(
    (row: T, index: number, e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('prevent-row-click')) return;
      onRowClick?.(row, index, e);
    },
    [onRowClick],
  );

  const handleRowKeyDown = useCallback(
    (e: React.KeyboardEvent, row: T, idx: number) => {
      if (!onRowClick) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onRowClick(row, idx);
      }
    },
    [onRowClick],
  );

  const Header = useMemo(
    () => (
      <thead className={cn('bg-gray-50', headerClassName)}>
        <tr>
          {colMeta.map(({ col, key, style, align }) => (
            <th
              onClick={() => col.onColumnHeaderClick?.(key)}
              key={`head-${key}`}
              scope="col"
              style={style}
              className={cn(
                'text-xs font-medium text-gray-500 uppercase tracking-wider select-none whitespace-normal break-words',
                compact ? 'px-4 py-2' : 'px-6 py-3',
                align === 'center' && 'text-center',
                align === 'right' && 'text-right',
                align === 'left' && 'text-left',
                col.headerClassName,
                col.onColumnHeaderClick && 'cursor-pointer',
              )}
            >
              <div className="flex items-center">
                {col.title}
                {col.sortable && (
                  <span className="text-xl">
                    {col.sortBy === key ? (col.sortOrder === 'ASC' ? '↑' : '↓') : ''}
                  </span>
                )}
              </div>
            </th>
          ))}
        </tr>
      </thead>
    ),
    [colMeta, compact, headerClassName],
  );

  // Loading skeleton
  if (loading) {
    return (
      <div className={cn('bg-white rounded-lg shadow overflow-hidden', className)} aria-busy>
        <TableSkeleton columns={columns} rowCount={skeletonRows} compact={compact} />
      </div>
    );
  }

  // Empty state
  if ((!data || data.length === 0) && emptyMessage) {
    return (
      <div className={cn('bg-white rounded-lg shadow overflow-hidden', className)}>
        <div className="px-6 py-12 text-center text-gray-500">
          <svg
            className="w-12 h-12 text-gray-400 mx-auto mb-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="text-lg font-medium text-gray-900 mb-2">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('bg-white rounded-lg shadow', className)}>
      <div className="overflow-x-auto">
        <table
          className={cn('min-w-full divide-y divide-gray-200', tableClassName)}
          role="table"
          aria-rowcount={data.length}
          aria-label={ariaLabel}
        >
          {Header}
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((row, rowIndex) => {
              const key = getRowKey(row, rowIndex);
              const clickable = !!onRowClick;
              const isSelected =
                selectedRowKey !== undefined && String(key) === String(selectedRowKey);

              return (
                <tr
                  key={String(key)}
                  className={getRowClassNames(row, rowIndex)}
                  onClick={clickable ? (e) => handleRowClick(row, rowIndex, e) : undefined}
                  onKeyDown={clickable ? (e) => handleRowKeyDown(e, row, rowIndex) : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  aria-selected={isSelected ? 'true' : 'false'}
                  data-selected={isSelected}
                >
                  {colMeta.map(({ col, key: colKey, style, align, cellClassName }) => (
                    <td
                      key={`cell-${colKey}`}
                      style={style}
                      className={cn(
                        'text-sm text-gray-900 align-middle whitespace-normal break-words',
                        compact ? 'px-4 py-2' : 'px-6 py-4',
                        align === 'center' && 'text-center',
                        align === 'right' && 'text-right',
                        align === 'left' && 'text-left',
                        cellClassName,
                      )}
                    >
                      {renderCell(col, row, rowIndex)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Экспортируем memo-обёртку с generic сигнатурой */
type MemoTableComponent = <T>(
  props: TableProps<T> & { selectedRowKey?: string | number },
) => JSX.Element;
const Table = memo(TableInner) as unknown as MemoTableComponent;

export default Table;
export { Table as MemoTable, TableInner as TableCore, TableSkeleton };
